import React, {useState} from "react";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import "./ListingDetail.scss";
import {AsyncImage} from "../../../components/AsyncImage";
import {SaveDealershipButton} from "../SaveDealershipButton";
import {useSelectListing} from "../useSelectListing";
import {guessColorDot} from "../utils";

const money = (n) => (n || n === 0) ? Number(n).toDollarFormat() : '—'
const dash  = (v) => (v === null || v === undefined || v === '') ? '—' : v

const CarFallback = () => (
    <div className="ld-photo-fallback">
        <svg xmlns="http://www.w3.org/2000/svg" width="120" height="60" viewBox="0 0 120 60" fill="none">
            <rect x="10" y="28" width="100" height="22" rx="4" fill="#cfd3da"/>
            <path d="M25 28 L35 12 L85 12 L95 28Z" fill="#cfd3da"/>
            <circle cx="30" cy="52" r="8" fill="#9aa0aa"/>
            <circle cx="90" cy="52" r="8" fill="#9aa0aa"/>
        </svg>
    </div>
)

// `swatch` is the normalized base color (e.g. "Black") — the dot comes from it,
// never from the marketing `value` text. Omitted/blank renders no dot.
const Spec = ({label, value, swatch}) => (
    <div className="ld-spec">
        <span className="ld-spec-label">{label}</span>
        <span className="ld-spec-value">
            {swatch && <span className="ld-color-dot" style={{background: guessColorDot(swatch)}}/>}
            {dash(value)}
        </span>
    </div>
)

const Section = ({title, children, className}) => (
    <section className={`ld-section ${className || ''}`}>
        <h3 className="ld-section-title">{title}</h3>
        {children}
    </section>
)

// `selection` carries the opener's onSelectListing when Details was reached from
// a deal / the calculator drawer (see ListingDetailModal); null everywhere else,
// where the shared hook falls back to the standalone new-tab flow.
export const ListingDetail = ({listing, selection}) => {
    const [activePhoto, setActivePhoto] = useState(0)
    const {selecting, selectListing} = useSelectListing(selection)

    if (!listing) return null

    const dealer  = listing.dealership || {}
    // Most expensive first; null/0-priced options fall to the end.
    const options = [...(listing.options || [])].sort((a, b) => (b.msrp || 0) - (a.msrp || 0))

    const photos = (listing.photo_urls && listing.photo_urls.length)
        ? listing.photo_urls
        : (listing.image_url ? [listing.image_url] : [])

    const title    = [listing.year, listing.make, listing.model].filter(Boolean).join(' ')
    const subtitle = [listing.trim, listing.version].filter(Boolean).join(' · ')
    const isNew    = String(listing.inventory_type || '').toLowerCase() === 'new'
    const optionsTotal = options.reduce((sum, o) => sum + (o.msrp || 0), 0)

    const mapUrl = (dealer.latitude && dealer.longitude)
        ? `https://www.google.com/maps/search/?api=1&query=${dealer.latitude},${dealer.longitude}`
        : null

    const hasMultiplePhotos = photos.length > 1
    const goPrev = (e) => {
        e.stopPropagation()
        setActivePhoto((i) => (i - 1 + photos.length) % photos.length)
    }
    const goNext = (e) => {
        e.stopPropagation()
        setActivePhoto((i) => (i + 1) % photos.length)
    }

    return (
        <div className="listing-detail">
            <div className="ld-top">
                {/* Gallery */}
                <div className="ld-gallery">
                    <div className="ld-photo-main">
                        {
                            photos.length
                                ? <AsyncImage
                                    key={photos[activePhoto]}
                                    src={photos[activePhoto]}
                                    alt={<CarFallback/>}
                                    width="100%"
                                  />
                                : <CarFallback/>
                        }
                        {
                            hasMultiplePhotos &&
                            <>
                                <button
                                    type="button"
                                    className="ld-carousel-arrow prev"
                                    onClick={goPrev}
                                    aria-label="Previous photo"
                                >
                                    <ChevronLeftIcon/>
                                </button>
                                <button
                                    type="button"
                                    className="ld-carousel-arrow next"
                                    onClick={goNext}
                                    aria-label="Next photo"
                                >
                                    <ChevronRightIcon/>
                                </button>
                                <div className="ld-carousel-counter">
                                    {activePhoto + 1} / {photos.length}
                                </div>
                            </>
                        }
                    </div>
                </div>

                {/* Headline */}
                <div className="ld-headline">
                    <div className="ld-badges">
                        <span className={`ld-badge ${isNew ? 'is-new' : 'is-used'}`}>
                            {isNew ? 'New' : 'Used'}
                        </span>
                        {listing.status && <span className="ld-badge is-status">{listing.status}</span>}
                    </div>

                    <h2 className="ld-title">{title}</h2>
                    {subtitle && <div className="ld-subtitle">{subtitle}</div>}

                    <div className="ld-price-block">
                        <div className="ld-price">{money(listing.price)}</div>
                        {
                            listing.combined_msrp &&
                            <div className="ld-msrp">MSRP {money(listing.combined_msrp)}</div>
                        }
                    </div>

                    <div className="ld-keyfacts">
                        <Spec label="Mileage" value={listing.miles != null ? `${Number(listing.miles).toLocaleString()} mi` : '—'}/>
                        <Spec label="Stock #" value={listing.stock_number}/>
                        <Spec label="VIN" value={listing.vin}/>
                        <Spec label="Listed" value={listing.inventory_date}/>
                    </div>

                    <div className="ld-cta-row">
                        {
                            listing.listing_url &&
                            <a className="ld-btn primary" href={listing.listing_url} target="_blank" rel="noopener noreferrer">
                                View on Dealer Site
                            </a>
                        }
                        {/* Same underlying flow as the listing card's Select
                            (useSelectListing). skipFetch: this record already
                            carries combined_msrp / inventory_type. */}
                        <button
                            type="button"
                            className="ld-btn ld-btn-select"
                            onClick={(e) => selectListing(listing, {event: e, skipFetch: true})}
                            disabled={selecting}
                        >
                            {selecting ? 'Selecting…' : 'Select Vehicle'}
                        </button>
                        {
                            listing.vehicle_history_url &&
                            <a className="ld-btn" href={listing.vehicle_history_url} target="_blank" rel="noopener noreferrer">
                                Vehicle History
                            </a>
                        }
                    </div>
                </div>
            </div>

            <div className="ld-grid">
                <Section title="Vehicle Details">
                    <div className="ld-spec-grid">
                        <Spec label="Body Type"    value={listing.body_type}/>
                        <Spec label="Drivetrain"   value={listing.drivetrain}/>
                        <Spec label="Fuel Type"    value={listing.fuel_type}/>
                        <Spec label="Powertrain"   value={listing.powertrain_type}/>
                        <Spec label="Transmission" value={listing.transmission}/>
                        <Spec label="Engine"       value={listing.engine}/>
                        <Spec label="Cylinders"    value={listing.cylinders}/>
                        <Spec label="Doors"        value={listing.doors}/>
                        <Spec label="Seating"      value={listing.seating_capacity}/>
                        <Spec label="Exterior"     value={listing.exterior_color} swatch={listing.base_exterior_color}/>
                        <Spec label="Interior"     value={listing.interior_color} swatch={listing.base_interior_color}/>
                    </div>
                </Section>

                <Section title="Pricing">
                    <div className="ld-spec-grid">
                        <Spec label="Selling Price" value={money(listing.price)}/>
                        <Spec label="Base MSRP"     value={money(listing.msrp)}/>
                        <Spec label="Combined MSRP" value={money(listing.combined_msrp)}/>
                    </div>
                </Section>

                <Section title={`Options${options.length ? ` (${options.length})` : ''}`} className="ld-options-section">
                    {
                        options.length
                            ? <div className="ld-options">
                                {options.map((o) => (
                                    <div className="ld-option-row" key={o.code || o.name}>
                                        <span className="ld-option-name">
                                            {dash(o.name)}
                                            {o.code && <span className="ld-option-code">{o.code}</span>}
                                        </span>
                                        <span className="ld-option-price">{money(o.msrp)}</span>
                                    </div>
                                ))}
                                <div className="ld-option-row ld-option-total">
                                    <span className="ld-option-name">Total Options</span>
                                    <span className="ld-option-price">{money(optionsTotal)}</span>
                                </div>
                              </div>
                            : <div className="ld-empty">No options listed.</div>
                    }
                </Section>

                <Section title="Dealer">
                    <div className="ld-dealer">
                        <div className="ld-dealer-name">
                            {dash(dealer.name)}
                            {dealer.id && <SaveDealershipButton dealership={dealer} size={18}/>}
                        </div>
                        <div className="ld-dealer-line">
                            {[dealer.city, dealer.state].filter(Boolean).join(', ') || '—'}
                        </div>
                        {dealer.phone && <a className="ld-dealer-line link" href={`tel:${dealer.phone}`}>{dealer.phone}</a>}
                        {mapUrl && <a className="ld-dealer-line link" href={mapUrl} target="_blank" rel="noopener noreferrer">View on map</a>}
                    </div>
                </Section>
            </div>
        </div>
    )
}
